import { Colophon } from '../sections/Colophon.js';
import styles from './LegalPage.module.css';

type LegalLocale = 'fr' | 'en';
type LegalKind = 'privacy' | 'terms' | 'data-deletion';

export interface PublicLegalRoute {
  kind: LegalKind;
  locale: LegalLocale;
}

interface PublicLegalSection {
  id: string;
  title: string;
  paragraphs: readonly string[];
  items?: readonly string[];
}

interface PublicLegalDocument {
  title: string;
  eyebrow: string;
  intro: string;
  status: string;
  updated: string;
  homeLabel: string;
  languageLabel: string;
  alternateHref: string;
  sectionsLabel: string;
  notice: string;
  sections: readonly PublicLegalSection[];
}

const CONTACT_EMAIL = 'contact@washedafrica.com';
// The paid beta runs on WhatsApp only (paid-beta spec §2); this is the business number customers
// write to. Non-breaking spaces keep the number on one line.
const WHATSAPP_NUMBER = '+228\u00a093\u00a067\u00a084\u00a025';

// Every claim below is checked against docs/specs/2026-09-27-paid-beta-operating-spec.md and the
// code it names (code wins). The source texts and their version history are
// docs/legal/terms-of-service-fr.md and docs/legal/privacy-policy-fr.md.
const DOCUMENTS: Record<LegalKind, Record<LegalLocale, PublicLegalDocument>> = {
  privacy: {
    fr: {
      title: 'Politique de confidentialité',
      eyebrow: 'Washed · données personnelles',
      intro:
        'Cette politique explique les données que Washed utilise à Lomé pour la liste d’attente, les candidatures de laveuses et le service, qui se commande et se gère sur WhatsApp pendant le bêta payant.',
      status: 'En vigueur',
      updated: '26 octobre 2026',
      homeLabel: 'Accueil',
      languageLabel: 'English',
      alternateHref: '/privacy',
      sectionsLabel: 'Sections de la politique',
      notice:
        'Washed est un projet commercial en cours de constitution à Lomé, Togo. Cette version décrit les traitements en cours et sera mise à jour si le service, nos prestataires ou les exigences applicables évoluent.',
      sections: [
        {
          id: 'responsable',
          title: 'Responsable et contact',
          paragraphs: [
            'Responsable du service : Washed, projet commercial en cours de constitution à Lomé, Togo.',
            `Pour toute question relative à vos données : ${CONTACT_EMAIL}, ou sur WhatsApp au ${WHATSAPP_NUMBER}.`,
          ],
        },
        {
          id: 'donnees',
          title: 'Données utilisées',
          paragraphs: [
            'Nous limitons la collecte aux informations nécessaires à la relation que vous avez avec Washed.',
          ],
          items: [
            'Liste d’attente : numéro de téléphone, quartier et adresse e-mail si vous choisissez de la fournir.',
            'Candidature laveuse : identité et coordonnées, quartier, informations nécessaires à la vérification et à l’organisation des visites.',
            'Votre foyer : numéro WhatsApp, prénom et nom, adresse et repères, le point de localisation que vous envoyez sur WhatsApp, le nombre de personnes inscrites et leurs prénoms si vous les donnez.',
            'Pièce d’identité du titulaire : elle est vue en personne, jamais photographiée. Nous notons seulement son type, ses 4 derniers caractères et sa date d’expiration.',
            'Abonnement et paiements : formule, cycles, commandes, numéro qui a payé, opérateur Mobile Money, référence de la transaction, montants et captures d’écran envoyées comme preuve de paiement.',
            'Visites : date, laveuse affectée et résultat de chaque visite, notés par le bureau. Pendant le bêta, la laveuse n’utilise pas d’application : aucune position GPS et aucune photo ne sont prises pendant la visite. Si cela change, cette politique sera mise à jour avant.',
            'Messages : vos échanges avec Washed sur WhatsApp, vos réclamations et les réponses du bureau.',
            'Données techniques : informations minimales nécessaires à la sécurité du site et de nos systèmes et au diagnostic des erreurs.',
          ],
        },
        {
          id: 'finalites',
          title: 'Pourquoi',
          paragraphs: [
            'Nous utilisons ces données pour gérer la liste d’attente, traiter les candidatures, reconnaître votre foyer à partir de votre numéro WhatsApp, planifier les visites, vérifier les paiements, payer les laveuses, traiter les réclamations et protéger le service contre les abus.',
            'Sur WhatsApp, un assistant automatique lit vos messages pour comprendre votre demande, avec l’aide d’un modèle d’intelligence artificielle. Il ne vérifie pas l’argent, n’attribue pas les places et ne décide d’aucune faute ni sanction : ce sont nos règles fixes ou une personne du bureau qui le font. Vous pouvez demander à parler à une personne à tout moment.',
            'Washed ne vend pas vos données personnelles et ne les utilise pas pour de la publicité ciblée.',
          ],
        },
        {
          id: 'prestataires',
          title: 'Prestataires techniques',
          paragraphs: [
            'Certaines données passent par les prestataires strictement nécessaires : WhatsApp (Meta) pour les messages, un fournisseur de modèle d’intelligence artificielle pour comprendre vos messages, l’hébergement de nos serveurs, l’envoi d’e-mails et le diagnostic d’erreurs. Ils ne reçoivent que les informations nécessaires à leur rôle.',
            'WhatsApp garde sa propre copie de vos conversations, selon ses propres conditions ; Washed ne peut pas l’effacer.',
            'Vous payez vous-même depuis votre compte Mixx by Yas ou Flooz. Washed ne prélève jamais d’argent sur votre compte et ne demande jamais votre code secret Mobile Money.',
          ],
        },
        {
          id: 'securite',
          title: 'Sécurité et conservation',
          paragraphs: [
            'Nous appliquons des contrôles techniques et organisationnels adaptés au stade du service : limitation des accès, journalisation des actions sensibles, chiffrement des données sensibles lorsque prévu par l’architecture et séparation des rôles opérationnels.',
            'Nous conservons les données uniquement pendant la durée nécessaire au service, à la sécurité, au traitement d’un litige ou aux obligations comptables et légales applicables. Les durées sont les suivantes ; à leur terme, les données sont supprimées ou anonymisées.',
          ],
          items: [
            'Numéro de téléphone et profil : tant que votre compte existe, puis 2 ans.',
            'Adresse de service et repères : tant que votre compte existe, puis 1 an. À la suppression du compte, le point exact est effacé ; seule une position arrondie à environ 1 km est gardée.',
            'Pièce d’identité du titulaire : les caractères notés sont effacés avec le compte.',
            'Messages WhatsApp : tant que votre compte existe ; leur texte est effacé avec le compte, sauf vos mots repris dans une réclamation.',
            'Visites et leur résultat : 10 ans, sans votre nom ni votre numéro après la suppression du compte, pour la paie des laveuses et la comptabilité.',
            'Notes et commentaires sur les visites : 2 ans.',
            'Réclamations, litiges et incidents de sécurité : 3 ans.',
            'Journaux techniques d’envoi des messages : 90 jours.',
            'Paiements et reçus : 10 ans, comme l’exige le droit comptable OHADA. Le numéro qui a payé reste sur le paiement : c’est vers lui qu’un remboursement est fait.',
          ],
        },
        {
          id: 'droits',
          title: 'Vos choix et vos droits',
          paragraphs: [
            `Vous pouvez demander l’accès à vos données, leur correction ou leur suppression sur WhatsApp au ${WHATSAPP_NUMBER}, ou par e-mail à ${CONTACT_EMAIL}. Pour un foyer, seul le titulaire du compte peut le demander. Nous vérifions que la demande vient bien de lui : depuis son propre numéro WhatsApp, en personne, ou en le rappelant sur ce numéro.`,
            'La copie de vos données vous est envoyée sur votre numéro WhatsApp. Vous pouvez quitter la liste d’attente à tout moment.',
          ],
        },
      ],
    },
    en: {
      title: 'Privacy policy',
      eyebrow: 'Washed · personal data',
      intro:
        'This policy explains the data Washed uses in Lomé for the waitlist, washerwoman applications and the service, which is ordered and managed on WhatsApp during the paid beta.',
      status: 'In effect',
      updated: '26 October 2026',
      homeLabel: 'Home',
      languageLabel: 'Français',
      alternateHref: '/confidentialite',
      sectionsLabel: 'Policy sections',
      notice:
        'Washed is a commercial project in the process of being established in Lomé, Togo. This version describes the processing in place today and will be updated if the service, our providers or applicable requirements change.',
      sections: [
        {
          id: 'controller',
          title: 'Responsible party and contact',
          paragraphs: [
            'Service operator: Washed, a commercial project in the process of being established in Lomé, Togo.',
            `For questions about your personal data: ${CONTACT_EMAIL}, or on WhatsApp at ${WHATSAPP_NUMBER}.`,
          ],
        },
        {
          id: 'data',
          title: 'Data we use',
          paragraphs: [
            'We limit collection to information needed for your relationship with Washed.',
          ],
          items: [
            'Waitlist: phone number, neighbourhood and email address if you choose to provide it.',
            'Washerwoman application: identity and contact information, neighbourhood, and information needed for vetting and visit organization.',
            'Your household: WhatsApp number, first and last name, address and landmarks, the location pin you send on WhatsApp, the number of people covered and their first names if you give them.',
            'The account holder’s ID document: it is seen in person, never photographed. We note only its type, its last 4 characters and its expiry date.',
            'Subscription and payments: plan, cycles, orders, the number that paid, the Mobile Money provider, the transaction reference, amounts and screenshots sent as proof of payment.',
            'Visits: the date, the washerwoman assigned and the outcome of each visit, noted by the office. During the beta the washerwoman uses no app: no GPS position and no photo is taken during the visit. If that changes, this policy will be updated first.',
            'Messages: your conversations with Washed on WhatsApp, your claims and the office’s replies.',
            'Technical data: the minimum information needed for the security of the site and our systems and for error diagnosis.',
          ],
        },
        {
          id: 'purposes',
          title: 'Why we use it',
          paragraphs: [
            'We use this data to manage the waitlist, process applications, recognise your household from your WhatsApp number, schedule visits, verify payments, pay washerwomen, handle claims and protect the service from abuse.',
            'On WhatsApp, an automated assistant reads your messages to understand your request, with the help of an artificial-intelligence model. It does not verify money, allocate places or decide any fault or sanction: our fixed rules or a person at the office do that. You can ask to talk to a person at any time.',
            'Washed does not sell personal data and does not use it for targeted advertising.',
          ],
        },
        {
          id: 'providers',
          title: 'Technical providers',
          paragraphs: [
            'Some data passes through providers strictly needed for the service: WhatsApp (Meta) for messages, an artificial-intelligence model provider to understand your messages, hosting for our servers, email delivery and error diagnosis. They receive only the information required for their role.',
            'WhatsApp keeps its own copy of your conversations under its own terms; Washed cannot delete it.',
            'You pay yourself from your Mixx by Yas or Flooz account. Washed never takes money from your account and never asks for your Mobile Money secret code.',
          ],
        },
        {
          id: 'security',
          title: 'Security and retention',
          paragraphs: [
            'We apply technical and organizational controls appropriate to the stage of the service, including access restrictions, logging of sensitive actions, encryption of sensitive data where specified by the architecture and separation of operational roles.',
            'We keep data only as long as needed for the service, security, dispute handling or applicable accounting and legal obligations. The periods are below; when they end, the data is deleted or anonymized.',
          ],
          items: [
            'Phone number and profile: while your account exists, then 2 years.',
            'Service address and landmarks: while your account exists, then 1 year. When the account is deleted, the exact pin is erased; only a position rounded to about 1 km is kept.',
            'The account holder’s ID document: the characters noted are erased with the account.',
            'WhatsApp messages: while your account exists; their text is erased with the account, except your words quoted in a claim.',
            'Visits and their outcome: 10 years, without your name or number once the account is deleted, for washerwoman pay and accounting.',
            'Visit ratings and comments: 2 years.',
            'Claims, disputes and safety incidents: 3 years.',
            'Technical message delivery logs: 90 days.',
            'Payments and receipts: 10 years, as OHADA accounting law requires. The number that paid stays on the payment: any refund is sent back to it.',
          ],
        },
        {
          id: 'rights',
          title: 'Your choices and rights',
          paragraphs: [
            `You may request access to, correction of or deletion of your data on WhatsApp at ${WHATSAPP_NUMBER}, or by email to ${CONTACT_EMAIL}. For a household, only the account holder may ask. We check that the request comes from them: from their own WhatsApp number, in person, or by calling them back on that number.`,
            'The copy of your data is sent to your WhatsApp number. You may leave the waitlist at any time.',
          ],
        },
      ],
    },
  },
  terms: {
    fr: {
      title: 'Conditions d’utilisation',
      eyebrow: 'Washed · conditions',
      intro:
        'Ces conditions s’appliquent au site, à la liste d’attente et au service Washed à Lomé, qui se commande et se gère sur WhatsApp pendant le bêta payant. Elles ne promettent pas qu’une laveuse ou une date de début de service particulière est déjà disponible.',
      status: 'En vigueur',
      updated: '26 octobre 2026',
      homeLabel: 'Accueil',
      languageLabel: 'English',
      alternateHref: '/terms',
      sectionsLabel: 'Sections des conditions',
      notice: `Washed est un projet commercial en cours de constitution à Lomé, Togo. Pendant le bêta payant, le service se commande, se paie et se gère uniquement sur WhatsApp, au ${WHATSAPP_NUMBER} ; aucune application n’est nécessaire.`,
      sections: [
        {
          id: 'service',
          title: 'Ce qu’est Washed',
          paragraphs: [
            'Washed organise des visites de lavage au domicile du client, réalisées par des laveuses indépendantes. Vous achetez le service à Washed, pas à une laveuse. Le linge reste au domicile et le foyer fournit l’eau, le savon et la bassine.',
            'L’inscription à la liste d’attente exprime un intérêt ; elle ne garantit ni une date, ni une laveuse, ni la disponibilité du service, et ne constitue pas une réservation de service payant.',
          ],
        },
        {
          id: 'inscription',
          title: 'Liste d’attente',
          paragraphs: [
            'Vous devez fournir des coordonnées exactes et utiliser vos propres informations. Nous pouvons supprimer les inscriptions manifestement frauduleuses, automatisées ou abusives.',
            'Vous pouvez demander le retrait de votre inscription à tout moment en nous contactant.',
          ],
        },
        {
          id: 'zone',
          title: 'Zone desservie',
          paragraphs: [
            'Le service Washed couvre tous les quartiers de Lomé. Nous pouvons modifier le calendrier ou l’étendue du service, notamment selon la capacité disponible des laveuses et l’état opérationnel du service.',
          ],
        },
        {
          id: 'whatsapp',
          title: 'Sur WhatsApp',
          paragraphs: [
            'L’abonnement appartient au foyer. La personne qui l’ouvre sur WhatsApp en est le titulaire : c’est elle qui commande, met en pause ou arrête l’abonnement. Elle peut demander au bureau d’autoriser un autre numéro. Payer pour un foyer ne donne aucun droit sur son compte.',
            'Un assistant automatique répond à vos messages. Il ne vérifie pas l’argent et ne prend pas les décisions importantes : ce sont nos règles fixes ou une personne du bureau qui le font. Vous pouvez demander à parler à une personne à tout moment.',
          ],
        },
        {
          id: 'offre',
          title: 'Offre et prix',
          paragraphs: [
            'Les formules et les prix sont ceux de la page Tarifs du site. Un cycle dure 30 jours. Le prix exact de votre foyer vous est donné sur WhatsApp avant tout paiement. Tant qu’un abonnement payant n’a pas été effectivement proposé et accepté, ces prix ne constituent pas une promesse de fourniture immédiate.',
            'Le prix couvre le linge habituel des personnes inscrites, lavé pendant la visite. Le linge d’autres maisons ou d’un commerce n’est pas compris. Le linge n’est ni pesé ni compté ; un volume nettement inhabituel est examiné par le bureau.',
            'L’objectif opérationnel est de conserver la même laveuse auprès d’un foyer sur les visites récurrentes. Une autre laveuse peut toutefois être affectée en cas d’indisponibilité, de capacité, de sécurité, de qualité ou à la demande du foyer.',
          ],
        },
        {
          id: 'identite',
          title: 'Pièce d’identité',
          paragraphs: [
            'Avant la première visite, le titulaire montre une pièce d’identité à la laveuse ou au bureau. Elle n’est jamais photographiée : nous notons seulement son type, ses 4 derniers caractères et sa date d’expiration. Sans cette vérification, la laveuse ne commence pas et la visite est comptée comme empêchée par le foyer.',
          ],
        },
        {
          id: 'paiement',
          title: 'Paiement',
          paragraphs: [
            'Vous payez vous-même par Mobile Money, Mixx by Yas ou Flooz, vers le numéro Washed indiqué sur WhatsApp. Washed ne prélève jamais d’argent sur votre compte. Ne donnez jamais votre code secret Mobile Money, ni à Washed ni à une laveuse, et ne payez jamais une laveuse directement.',
            'Le bureau vérifie chaque paiement ; une capture d’écran ne suffit pas à elle seule. Une visite n’est confirmée que lorsque le cycle qui la couvre est payé.',
            'Avant la fin de chaque cycle, nous vous rappelons sur WhatsApp le montant à payer. Sans paiement 5 jours après l’échéance, l’abonnement est mis en pause, sans aucune somme due pour les jours non servis. Le cycle suivant commence le jour où votre paiement est vérifié.',
          ],
        },
        {
          id: 'pause',
          title: 'Pause',
          paragraphs: [
            'Vous pouvez mettre l’abonnement en pause sur WhatsApp. Le cycle déjà payé se termine normalement, avec ses visites. Pendant la pause, vous ne payez rien et aucune visite n’a lieu.',
            'Pour reprendre, écrivez-nous : un nouveau cycle commence au paiement. Votre ancien jour de passage n’est pas garanti. Une pause ne devient jamais une résiliation.',
          ],
        },
        {
          id: 'arret',
          title: 'Arrêt de l’abonnement',
          paragraphs: [
            'Arrêter l’abonnement est gratuit. Par défaut, il s’arrête à la fin du cycle déjà payé et les visites restantes de ce cycle sont maintenues.',
            'Vous pouvez demander un arrêt immédiat ; un responsable du bureau l’examine. S’il est accordé, chaque visite payée et non utilisée vous est remboursée, à sa part du prix du cycle. Une visite déjà faite, ou comptée comme utilisée, n’est pas remboursée.',
          ],
        },
        {
          id: 'visites',
          title: 'Visite déplacée ou manquée',
          paragraphs: [
            'Pour changer la date d’une visite, écrivez-nous sur WhatsApp, si possible au moins 24 heures avant. Aucun frais n’est demandé pour un changement de date, même tardif.',
            'Si personne n’ouvre 15 minutes après l’arrivée de la laveuse malgré nos appels, ou si l’eau, le savon ou la bassine manquent, la visite est en règle générale comptée comme utilisée.',
            'Si la laveuse ne vient pas, ou si Washed annule, la visite vous reste due : le bureau la reprogramme en priorité et elle n’expire pas.',
            'Une laveuse peut quitter une visite à tout moment si elle estime sa sécurité menacée. Le bureau examine alors ce qui s’est passé avant de décider du sort de la visite.',
          ],
        },
        {
          id: 'probleme',
          title: 'Signaler un problème',
          paragraphs: [
            'Signalez tout problème sur WhatsApp : retard, linge abîmé ou manquant, comportement. Une personne du bureau examine chaque réclamation et vous répond sur WhatsApp.',
          ],
        },
        {
          id: 'respect',
          title: 'Respect et sécurité',
          paragraphs: [
            'Washed peut refuser ou interrompre une relation lorsque la sécurité d’un foyer, d’une laveuse, d’un membre de l’équipe ou de la plateforme est en cause. Les utilisateurs et les laveuses doivent se traiter avec respect et ne pas utiliser le service pour une activité illégale, abusive ou frauduleuse.',
          ],
        },
        {
          id: 'donnees',
          title: 'Données personnelles',
          paragraphs: [
            'Vos données sont traitées selon la politique de confidentialité publiée sur ce site.',
          ],
        },
        {
          id: 'contact',
          title: 'Contact',
          paragraphs: [
            `Questions relatives à ces conditions : ${CONTACT_EMAIL}, ou sur WhatsApp au ${WHATSAPP_NUMBER}.`,
          ],
        },
      ],
    },
    en: {
      title: 'Terms of use',
      eyebrow: 'Washed · terms',
      intro:
        'These terms apply to the website, the waitlist and the Washed service in Lomé, which is ordered and managed on WhatsApp during the paid beta. They do not promise that a particular washerwoman or service start date is already available.',
      status: 'In effect',
      updated: '26 October 2026',
      homeLabel: 'Home',
      languageLabel: 'Français',
      alternateHref: '/conditions',
      sectionsLabel: 'Terms sections',
      notice: `Washed is a commercial project in the process of being established in Lomé, Togo. During the paid beta, the service is ordered, paid for and managed only on WhatsApp, at ${WHATSAPP_NUMBER}; no app is needed.`,
      sections: [
        {
          id: 'service',
          title: 'What Washed is',
          paragraphs: [
            'Washed organizes laundry visits at the customer’s home, carried out by independent washerwomen. You buy the service from Washed, not from a washerwoman. Laundry remains at the home and the household provides the water, the soap and the basin.',
            'Joining the waitlist expresses interest; it does not guarantee a date, a washerwoman or service availability, and does not reserve a paid service.',
          ],
        },
        {
          id: 'signup',
          title: 'Waitlist',
          paragraphs: [
            'You must provide accurate contact information and use your own details. We may remove signups that are clearly fraudulent, automated or abusive.',
            'You may ask to be removed from the waitlist at any time by contacting us.',
          ],
        },
        {
          id: 'rollout',
          title: 'Service area',
          paragraphs: [
            'The Washed service covers every neighbourhood of Lomé. We may change the timing or scope of the service, in particular based on available washerwoman capacity and operational readiness.',
          ],
        },
        {
          id: 'whatsapp',
          title: 'On WhatsApp',
          paragraphs: [
            'The subscription belongs to the household. The person who opens it on WhatsApp is the account holder: they order, pause or stop the subscription. They may ask the office to authorise another number. Paying for a household gives no rights over its account.',
            'An automated assistant answers your messages. It does not verify money and does not take important decisions: our fixed rules or a person at the office do that. You can ask to talk to a person at any time.',
          ],
        },
        {
          id: 'offer',
          title: 'Offer and prices',
          paragraphs: [
            'Plans and prices are those on the website’s Pricing page. A cycle lasts 30 days. The exact price for your household is given to you on WhatsApp before any payment. Until a paid subscription is actually offered and accepted, these prices do not constitute a promise of immediate service.',
            'The price covers the usual laundry of the people registered, washed during the visit. Laundry from other homes or from a business is not included. Laundry is neither weighed nor counted; a clearly unusual volume is reviewed by the office.',
            'The operating goal is to keep the same washerwoman with a household across recurring visits. Another washerwoman may be assigned because of availability, capacity, safety, quality or at the household’s request.',
          ],
        },
        {
          id: 'identity',
          title: 'ID document',
          paragraphs: [
            'Before the first visit, the account holder shows an ID document to the washerwoman or at the office. It is never photographed: we note only its type, its last 4 characters and its expiry date. Without this check, the washerwoman does not start and the visit counts as prevented by the household.',
          ],
        },
        {
          id: 'payment',
          title: 'Payment',
          paragraphs: [
            'You pay yourself by Mobile Money, Mixx by Yas or Flooz, to the Washed number given on WhatsApp. Washed never takes money from your account. Never give your Mobile Money secret code to Washed or to a washerwoman, and never pay a washerwoman directly.',
            'The office checks every payment; a screenshot alone is not enough. A visit is confirmed only once the cycle that covers it is paid.',
            'Before each cycle ends, we remind you on WhatsApp of the amount to pay. Without payment 5 days after the due date, the subscription is paused, with nothing owed for days not served. The next cycle starts on the day your payment is verified.',
          ],
        },
        {
          id: 'pause',
          title: 'Pause',
          paragraphs: [
            'You can pause the subscription on WhatsApp. The cycle already paid ends normally, with its visits. During the pause you pay nothing and no visit takes place.',
            'To restart, write to us: a new cycle starts when you pay. Your previous visit day is not guaranteed. A pause never turns into a cancellation.',
          ],
        },
        {
          id: 'stop',
          title: 'Stopping the subscription',
          paragraphs: [
            'Stopping the subscription is free. By default it stops at the end of the cycle already paid, and the remaining visits of that cycle go ahead.',
            'You may ask to stop immediately; an office supervisor reviews the request. If it is granted, each paid visit not yet used is refunded at its share of the cycle price. A visit already done, or counted as used, is not refunded.',
          ],
        },
        {
          id: 'visits',
          title: 'Moved or missed visits',
          paragraphs: [
            'To change the date of a visit, write to us on WhatsApp, if possible at least 24 hours before. No fee is charged for a change of date, even a late one.',
            'If nobody opens 15 minutes after the washerwoman arrives despite our calls, or if the water, soap or basin is missing, the visit is as a rule counted as used.',
            'If the washerwoman does not come, or if Washed cancels, the visit is still owed to you: the office reschedules it as a priority and it does not expire.',
            'A washerwoman may leave a visit at any time if she believes her safety is at risk. The office then reviews what happened before deciding what happens to the visit.',
          ],
        },
        {
          id: 'problem',
          title: 'Reporting a problem',
          paragraphs: [
            'Report any problem on WhatsApp: lateness, damaged or missing laundry, behaviour. A person at the office reviews every claim and answers you on WhatsApp.',
          ],
        },
        {
          id: 'respect',
          title: 'Respect and safety',
          paragraphs: [
            'Washed may refuse or stop a relationship when the safety of a household, washerwoman, team member or the platform is at risk. Users and washerwomen must treat one another respectfully and must not use the service for illegal, abusive or fraudulent activity.',
          ],
        },
        {
          id: 'data',
          title: 'Personal data',
          paragraphs: ['Your data is handled under the privacy policy published on this site.'],
        },
        {
          id: 'contact',
          title: 'Contact',
          paragraphs: [
            `Questions about these terms: ${CONTACT_EMAIL}, or on WhatsApp at ${WHATSAPP_NUMBER}.`,
          ],
        },
      ],
    },
  },
  'data-deletion': {
    fr: {
      title: 'Suppression de vos données',
      eyebrow: 'Washed · vos données',
      intro:
        'Vous pouvez demander la suppression des données associées à votre inscription, candidature ou compte Washed.',
      status: 'En vigueur',
      updated: '26 octobre 2026',
      homeLabel: 'Accueil',
      languageLabel: 'English',
      alternateHref: '/data-deletion',
      sectionsLabel: 'Comment procéder',
      notice:
        'Nous devons parfois conserver certaines informations lorsque cela est nécessaire à la sécurité, à un litige ou à une obligation comptable ou légale applicable. Dans ce cas, nous limitons leur usage à cette finalité.',
      sections: [
        {
          id: 'demande',
          title: 'Faire une demande',
          paragraphs: [
            `Écrivez-nous sur WhatsApp au ${WHATSAPP_NUMBER} depuis le numéro de votre compte, ou à ${CONTACT_EMAIL} en indiquant ce numéro. Précisez si votre demande concerne la liste d’attente, une candidature laveuse ou un foyer abonné.`,
            'Pour un foyer, seul le titulaire du compte peut demander la suppression.',
          ],
        },
        {
          id: 'verification',
          title: 'Vérification',
          paragraphs: [
            'Pour éviter qu’un tiers ne supprime vos données à votre place, nous vérifions que la demande vient du titulaire : depuis son propre numéro WhatsApp, en personne, ou en le rappelant sur ce numéro.',
          ],
        },
        {
          id: 'avant',
          title: 'Avant la suppression',
          paragraphs: [
            'Un abonnement en cours, une visite à venir, un remboursement ou une réclamation non réglés doivent d’abord être terminés. Nous vous disons ce qui reste à régler. Une suspension en cours pour raison de sécurité empêche la suppression.',
            'Après la suppression, Washed ne peut plus vous écrire. Si le même numéro nous écrit à nouveau, il ouvre un nouveau compte.',
          ],
        },
        {
          id: 'traitement',
          title: 'Traitement',
          paragraphs: [
            'Nous effaçons ce qui vous identifie : numéros, noms, adresse, repères, caractères notés de votre pièce d’identité et texte des messages. Le point de localisation est arrondi à environ 1 km.',
            'Nous gardons, sans votre nom, ce que la loi ou la sécurité exigent : paiements, cycles et visites pendant 10 ans (droit comptable OHADA), réclamations et incidents de sécurité pendant 3 ans. Le numéro qui a payé reste sur les paiements. Ces informations ne sont plus utilisées pour les finalités ordinaires du service.',
            'WhatsApp garde sa propre copie de vos conversations ; Washed ne peut pas l’effacer.',
          ],
        },
      ],
    },
    en: {
      title: 'Delete your data',
      eyebrow: 'Washed · your data',
      intro:
        'You can request deletion of data associated with your Washed signup, application or account.',
      status: 'In effect',
      updated: '26 October 2026',
      homeLabel: 'Home',
      languageLabel: 'Français',
      alternateHref: '/suppression-donnees',
      sectionsLabel: 'How to proceed',
      notice:
        'We may sometimes need to retain limited information where necessary for security, a dispute, or an applicable accounting or legal obligation. In that case, its use is restricted to that purpose.',
      sections: [
        {
          id: 'request',
          title: 'Make a request',
          paragraphs: [
            `Write to us on WhatsApp at ${WHATSAPP_NUMBER} from your account’s number, or to ${CONTACT_EMAIL} giving that number. Tell us whether the request concerns the waitlist, a washerwoman application or a subscribed household.`,
            'For a household, only the account holder may request deletion.',
          ],
        },
        {
          id: 'verification',
          title: 'Verification',
          paragraphs: [
            'To prevent someone else from deleting your data, we check that the request comes from the account holder: from their own WhatsApp number, in person, or by calling them back on that number.',
          ],
        },
        {
          id: 'before',
          title: 'Before deletion',
          paragraphs: [
            'A running subscription, an upcoming visit, or an unsettled refund or claim must be finished first. We tell you what is left to settle. A safety suspension in force prevents deletion.',
            'After deletion, Washed can no longer write to you. If the same number writes to us again, it opens a new account.',
          ],
        },
        {
          id: 'processing',
          title: 'Processing the request',
          paragraphs: [
            'We erase what identifies you: numbers, names, address, landmarks, the characters noted from your ID document and the text of messages. The location pin is rounded to about 1 km.',
            'We keep, without your name, what the law or safety requires: payments, cycles and visits for 10 years (OHADA accounting law), claims and safety incidents for 3 years. The number that paid stays on the payments. This information is no longer used for ordinary service purposes.',
            'WhatsApp keeps its own copy of your conversations; Washed cannot delete it.',
          ],
        },
      ],
    },
  },
};

export function PublicLegalPage({ route }: { route: PublicLegalRoute }) {
  const document = DOCUMENTS[route.kind][route.locale];

  return (
    <div className={styles.page}>
      <div className={styles.navWrap}>
        <nav
          className={styles.nav}
          aria-label={route.locale === 'fr' ? 'Navigation' : 'Navigation'}
        >
          <a className={styles.brand} href="/">
            washed<span className={styles.dot}>.</span>
          </a>
          <div className={styles.navLinks}>
            <a href="/">{document.homeLabel}</a>
            <a className={styles.langToggle} href={document.alternateHref}>
              {document.languageLabel}
            </a>
          </div>
        </nav>
      </div>

      <main className={styles.main}>
        <p className={`smallcaps ${styles.eyebrow}`}>{document.eyebrow}</p>
        <h1 className={styles.h1}>{document.title}</h1>
        <p className={styles.intro}>{document.intro}</p>
        <p className={styles.updateBanner}>{document.notice}</p>

        <dl className={styles.meta}>
          <div className={styles.metaItem}>
            <dt>{route.locale === 'fr' ? 'Statut' : 'Status'}</dt>
            <dd>{document.status}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt>{route.locale === 'fr' ? 'Mise à jour' : 'Updated'}</dt>
            <dd>{document.updated}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt>{route.locale === 'fr' ? 'Contact' : 'Contact'}</dt>
            <dd>
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </dd>
          </div>
        </dl>

        <nav className={styles.quickNav} aria-label={document.sectionsLabel}>
          {document.sections.map((section) => (
            <a href={`#${section.id}`} key={section.id}>
              {section.title}
            </a>
          ))}
        </nav>

        {document.sections.map((section) => (
          <section className={styles.section} id={section.id} key={section.id}>
            <h2>{section.title}</h2>
            <div className={styles.body}>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.items && (
                <ul>
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        ))}
      </main>

      <Colophon locale={route.locale} />
    </div>
  );
}
